import { AppDataProvider, FreshnessNotice } from './data/freshness';
import { watchRtdbReconnect } from './data/rtdb-connection';
import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import { setViewerTimezone } from '@shared/utils';
import i18n from '@shared/i18n';
import { loadFirebaseAuth } from '@shared/firebase-loader';
import type { UpdaterStatus, UserSession } from '@shared/types';
import {
  flushAnalytics,
  initAnalytics,
  resetAnalytics,
  setAnalyticsActivity,
  setAnalyticsUserProperties,
  trackEvent,
} from '@/lib/analytics';
import Titlebar from './components/layout/Titlebar';
import SplashScreen from './components/SplashScreen';
import { usePlatformServices } from './platform/context';
import { resolveStoredSessionDecision, withSessionActivity } from './session-retention';
import { claimFirstOpen } from './splash-mode';
import type { NavControls } from './components/MainShell';

const LoginScreen = lazy(() => import('./components/auth/LoginScreen'));
const MainShell = lazy(() => import('./components/MainShell'));
const EmployeeShell = lazy(() => import('./components/EmployeeShell'));
const OnboardingFlow = lazy(() => import('./components/onboarding/OnboardingFlow'));
const UpdatePopup = lazy(() => import('./components/updater/UpdatePopup'));

const SPLASH_LOADER_REVEAL_MS = 800;
const SPLASH_LOADER_PULSE_MS = 1500;
// Hold until the loading bar is visible long enough to complete one full pulse.
const SPLASH_MIN_VISIBLE_MS = SPLASH_LOADER_REVEAL_MS + SPLASH_LOADER_PULSE_MS;
const SPLASH_FAILSAFE_MS = 8000;

// Demo mode is a development-only tool. `import.meta.env.DEV` is true only under
// the Vite dev server (`npm run dev`) and false in every packaged desktop build
// and deployed web build — so clients can never accidentally activate it.
const DEMO_MODE_AVAILABLE = import.meta.env.DEV;

type AppAuthSessionStatus =
  | { status: 'available'; uid: string }
  | { status: 'missing'; reason: 'no-user' | 'anonymous' }
  | { status: 'unsupported'; uid: string }
  | { status: 'unavailable'; uid: string; error: unknown };

function getStoredSessionFirebaseUid(session: UserSession): string {
  const emailKey = session.emailKey || String(session.email || '').trim().toLowerCase().replace(/\./g, '||');
  return `desktop:${emailKey}`;
}

function doesFirebaseUserMatchStoredSession(
  user: { uid?: string | null } | null | undefined,
  session: UserSession,
): boolean {
  return Boolean(user?.uid && user.uid === getStoredSessionFirebaseUid(session));
}

function getFirebaseAuthStateLabel(
  status: AppAuthSessionStatus,
  user: { uid?: string | null } | null | undefined,
  session: UserSession,
): 'available' | 'missing' | 'anonymous' | 'unverified' | 'temporarily_unavailable' | 'mismatched' {
  if (status.status === 'available') return 'available';
  if (status.status === 'missing') return status.reason === 'anonymous' ? 'anonymous' : 'missing';
  if (user?.uid && !doesFirebaseUserMatchStoredSession(user, session)) return 'mismatched';
  if (status.status === 'unavailable') return 'temporarily_unavailable';
  return 'unverified';
}

export default function App() {
  const platformServices = usePlatformServices();
  const [session, setSession] = useState<UserSession | null>(null);
  const [loginErrorMessage, setLoginErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [splashVisible, setSplashVisible] = useState(true);
  const [splashExiting, setSplashExiting] = useState(false);
  // A reload of an open window skips the hold: the splash leaves as soon as the screen is ready.
  const [quietSplash] = useState(() => !claimFirstOpen());
  const [demoMode, setDemoMode] = useState(false);
  const [navControls, setNavControls] = useState<NavControls | null>(null);
  const [onboardingCompleted, setOnboardingCompleted] = useState<boolean | null>(null);
  const [updaterStatus, setUpdaterStatus] = useState<UpdaterStatus>({ state: 'idle' });
  const [updatePopupDismissed, setUpdatePopupDismissed] = useState(false);
  const [screenReady, setScreenReady] = useState(false);
  const splashStartedAtRef = useRef(Date.now());
  const appOpenedTrackedRef = useRef(false);
  const activityStateKnownRef = useRef(false);
  const lastActivityStateRef = useRef(true);


  // Stored timestamps are naive strings written in the USER's own frame, so the
  // frame has to be supplied when they are read back. Setting it here means the
  // fourteen timeAgo call sites do not each have to thread session.timezone,
  // and it follows every session change including sign-out. Deliberately during
  // render rather than in an effect: an effect runs AFTER the first paint that
  // has the session, and nothing would re-render to correct that paint.
  setViewerTimezone(session?.timezone);
  const markScreenReady = useCallback(() => {
    setScreenReady(true);
  }, []);
  const currentScreenKey = loading
    ? 'boot'
    : session
      ? !session.isManager
        ? 'employee-shell'
        : onboardingCompleted === false
          ? 'onboarding'
          : 'main-shell'
      : 'login';

  useEffect(() => {
    setScreenReady(false);
  }, [currentScreenKey]);

  useEffect(() => {
    const platform = platformServices.target === 'desktop' ? platformServices.os : platformServices.target;

    initAnalytics();
    setAnalyticsUserProperties({
      platform,
      locale: navigator.language,
    });
    if (!appOpenedTrackedRef.current) {
      appOpenedTrackedRef.current = true;
      trackEvent('app_opened', {
        platform,
        locale: navigator.language,
      });
    }
  }, [platformServices.os, platformServices.target]);

  const syncUpdaterAuth = useCallback(async (forceRefresh: boolean = false) => {
    try {
      const { getFirebaseIdToken } = await loadFirebaseAuth();
      const token = await getFirebaseIdToken(forceRefresh);
      await platformServices.updater.setAuthToken(token);
    } catch (error) {
      console.warn('[App] Failed to sync updater auth token', error);
    }
  }, [platformServices]);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        const storedSessionPromise = platformServices.storage.getSession();
        const preferencesPromise = platformServices.storage.getPreferences();

        let restoredSession = false;
        const [stored, prefs] = await Promise.all([storedSessionPromise, preferencesPromise]);
        const sessionDecision = resolveStoredSessionDecision(stored, prefs);
        if (cancelled) return;

        // Open a stored session straight away. Firebase's auth check reloads the user over
        // the network (two round trips when the token has expired, up to 30 s on a bad
        // connection), and the app has nothing to wait for: Firestore, the realtime
        // database and getFirebaseIdToken all wait for auth themselves. If the check
        // below fails, the manager is signed out to the login screen as before.
        if (stored && sessionDecision.action === 'restore') {
          await i18n.changeLanguage(stored.language || 'en');
          if (cancelled) return;
          if (prefs && prefs.onboardingCompleted === undefined) {
            // Auto-complete onboarding for existing users upgrading.
            prefs.onboardingCompleted = true;
            await platformServices.storage.setPreferences(prefs);
            if (cancelled) return;
          }
          setSession(stored);
          setLoginErrorMessage(null);
          setOnboardingCompleted(prefs?.onboardingCompleted === true);
          setLoading(false);
        }

        console.log('[App] Initializing Firebase...');
        const firebase = await loadFirebaseAuth();
        firebase.initFirebaseAuth();
        console.log('[App] Firebase auth initialized');
        const auth = firebase.getFirebaseAuth();
        await auth.authStateReady?.();
        if (cancelled) return;

        const currentFirebaseUser = auth.currentUser;
        const hasFirebaseUser = Boolean(currentFirebaseUser && !currentFirebaseUser.isAnonymous);
        const appAuthStatus: AppAuthSessionStatus = hasFirebaseUser
          ? await firebase.getAppAuthSessionStatus()
          : { status: 'missing', reason: currentFirebaseUser?.isAnonymous ? 'anonymous' : 'no-user' };
        const hasAppAuth = appAuthStatus.status === 'available';
        if (cancelled) return;

        if (sessionDecision.action === 'expire') {
          console.warn('[App] Clearing stored session after 30 days of inactivity');
          await platformServices.storage.clearSession();
          await firebase.clearFirebaseAuthSession();
          if (cancelled) return;
          setOnboardingCompleted(false);
          setLoginErrorMessage(null);
        } else if (stored) {
          const storedLanguage = stored.language || 'en';
          const restoreReason = sessionDecision.action === 'restore' ? sessionDecision.reason : 'legacy';

          const canRestoreStoredSession = hasAppAuth
            || (
              appAuthStatus.status === 'unavailable'
              && doesFirebaseUserMatchStoredSession(currentFirebaseUser, stored)
            );

          if (!canRestoreStoredSession) {
            const firebaseAuthState = getFirebaseAuthStateLabel(appAuthStatus, currentFirebaseUser, stored);
            console.warn(`[App] Stored session requires reauthentication (${firebaseAuthState})`);
            await platformServices.storage.clearSession();
            await firebase.clearFirebaseAuthSession();
            if (cancelled) return;
            setSession(null);
            setOnboardingCompleted(false);
            setLoginErrorMessage(i18n.t('auth:errorSessionExpired'));
            trackEvent('session_reauth_required', {
              language: storedLanguage,
              user_role: stored.isManager ? 'manager' : 'employee',
              firebase_auth: firebaseAuthState,
              restore_reason: restoreReason,
            });
            void flushAnalytics();
          } else {
            if (appAuthStatus.status === 'unavailable') {
              console.warn('[App] Restoring stored session while Firebase token check is temporarily unavailable', appAuthStatus.error);
            }
            console.log('[App] Stored session verified');
            restoredSession = true;
            trackEvent('session_restored', {
              language: storedLanguage,
              user_role: stored.isManager ? 'manager' : 'employee',
              onboarding_completed: prefs?.onboardingCompleted === true,
              firebase_auth: getFirebaseAuthStateLabel(appAuthStatus, currentFirebaseUser, stored),
              restore_reason: restoreReason,
            });
            void flushAnalytics();
          }
        } else {
          setOnboardingCompleted(false);
          setLoginErrorMessage(null);
        }

        if (platformServices.target === 'desktop') {
          void (async () => {
            if (restoredSession && hasAppAuth) {
              await syncUpdaterAuth(true);
              if (cancelled) return;
            }

            let currentUpdaterStatus: UpdaterStatus | null = null;

            try {
              currentUpdaterStatus = await platformServices.updater.getStatus();
            } catch (error) {
              console.warn('[App] Failed to read updater status during startup', error);
            }

            if (cancelled) return;

            if (currentUpdaterStatus?.state === 'downloaded') {
              setUpdaterStatus(currentUpdaterStatus);
            } else {
              const nextUpdaterStatus = await platformServices.updater.checkForUpdates();
              if (!cancelled && nextUpdaterStatus) {
                setUpdaterStatus(nextUpdaterStatus);
              }
            }
          })();
        }
      } catch (err) {
        console.error('[App] Init/session restore failed:', err);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    void init();

    return () => {
      cancelled = true;
    };
  }, [platformServices, syncUpdaterAuth]);

  const persistSessionActivity = useCallback(async (options: { force?: boolean } = {}) => {
    const preferences = await platformServices.storage.getPreferences();
    const nextPreferences = withSessionActivity(preferences, Date.now(), options);

    if (nextPreferences !== preferences) {
      await platformServices.storage.setPreferences(nextPreferences);
    }
  }, [platformServices.storage]);

  useEffect(() => {
    if (loading) {
      return;
    }

    let cancelled = false;
    let unsubscribe = () => {};

    void (async () => {
      const firebase = await loadFirebaseAuth();
      firebase.initFirebaseAuth();
      if (cancelled) return;

      unsubscribe = firebase.subscribeToIdTokenChanges(async (user) => {
        try {
          const token = user ? await firebase.getFirebaseIdToken() : null;
          await platformServices.updater.setAuthToken(token);
          if (token) {
            await flushAnalytics();
          }
        } catch (error) {
          console.warn('[App] Failed to sync updater auth token from auth state', error);
        }
      });
    })();

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [loading, platformServices]);

  useEffect(() => {
    let mounted = true;

    const unsubscribe = platformServices.updater.onStatus((status) => {
      if (mounted) setUpdaterStatus(status);
    });

    platformServices.updater.getStatus()
      .then((status) => {
        if (mounted && status) {
          setUpdaterStatus(status);
        }
      })
      .catch((error) => {
        console.warn('[App] Failed to read updater status', error);
      });

    let refreshTimer: number | null = null;

    if (session) {
      refreshTimer = window.setInterval(() => {
        syncUpdaterAuth(true).catch(() => {});
      }, 45 * 60 * 1000);

      syncUpdaterAuth().catch(() => {});
    }

    return () => {
      mounted = false;
      unsubscribe?.();
      if (refreshTimer !== null) {
        window.clearInterval(refreshTimer);
      }
    };
  }, [platformServices, session, syncUpdaterAuth]);

  // Reset popup dismissed state when a new version is detected
  useEffect(() => {
    if (updaterStatus.version || updaterStatus.updateId) setUpdatePopupDismissed(false);
  }, [updaterStatus.updateId, updaterStatus.version]);

  // When loading finishes, begin the splash exit animation
  useEffect(() => {
    if (!loading && !splashExiting) {
      const elapsedMs = Date.now() - splashStartedAtRef.current;
      const minRemainingMs = quietSplash ? 0 : Math.max(0, SPLASH_MIN_VISIBLE_MS - elapsedMs);
      const failsafeRemainingMs = Math.max(0, SPLASH_FAILSAFE_MS - elapsedMs);

      if ((screenReady && minRemainingMs === 0) || failsafeRemainingMs === 0) {
        setSplashExiting(true);
        return;
      }

      const holdTimer = setTimeout(() => {
        setSplashExiting(true);
      }, screenReady ? minRemainingMs : failsafeRemainingMs);
      return () => clearTimeout(holdTimer);
    }
  }, [loading, quietSplash, screenReady, splashExiting]);

  const handleSplashFinished = useCallback(() => {
    setSplashVisible(false);
  }, []);

  const handleLogin = useCallback(async (user: UserSession) => {
    await i18n.changeLanguage(user.language || 'en');
    // Read onboarding state before the session lands so both commit in one batch: init
    // parks onboardingCompleted at false while logged out, and setting the session first
    // would paint OnboardingFlow for a returning user until the prefs read resolved.
    const prefs = await platformServices.storage.getPreferences().catch(() => null);
    setOnboardingCompleted(prefs?.onboardingCompleted === true);
    setSession(user);
    setLoginErrorMessage(null);
    await platformServices.storage.setSession(user);
    await persistSessionActivity({ force: true });
    await syncUpdaterAuth(true);
    await flushAnalytics(true);

    const nextUpdaterStatus = await platformServices.updater.checkForUpdates();
    if (nextUpdaterStatus) {
      setUpdaterStatus(nextUpdaterStatus);
    }

    trackEvent('login_completed', {
      language: user.language || 'en',
      user_role: user.isManager ? 'manager' : 'employee',
      onboarding_completed: prefs?.onboardingCompleted === true,
    });
  }, [persistSessionActivity, platformServices, syncUpdaterAuth]);

  const handleLogout = useCallback(async () => {
    trackEvent('logout_completed', {
      platform: platformServices.target === 'desktop' ? platformServices.os : platformServices.target,
    });
    await flushAnalytics();
    setSession(null);
    const { clearFocusAreasCache } = await import('@shared/focus-areas');
    clearFocusAreasCache();
    setLoginErrorMessage(null);
    await platformServices.updater.setAuthToken(null);
    await platformServices.storage.clearSession();
    const { clearFirebaseAuthSession } = await loadFirebaseAuth();
    await clearFirebaseAuthSession();
    resetAnalytics();
    // Drop cached recipient profiles so a different user logging into this
    // window never inherits the previous user's cached personalization data.
    const { clearRecipientProfileCache } = await import('@shared/recipient-profile');
    clearRecipientProfileCache();
    const { clearUserProfileCache } = await import('@shared/cache-service');
    clearUserProfileCache();
    const { clearHistoryCache } = await import('@shared/recognition-history');
    clearHistoryCache();
    platformServices.notifications.updateTrayData({ feedbackCount: 0, boostCount: 0, checkedInToday: false, checkinStatus: 'unknown', nudgePreviews: [], language: 'en' });
  }, [platformServices]);

  useEffect(() => {
    if (!session) {
      return;
    }

    let cancelled = false;
    const touchIfActive = async (force: boolean = false) => {
      if (cancelled) {
        return;
      }

      try {
        await persistSessionActivity({ force });
      } catch (error) {
        console.warn('[App] Failed to persist session activity', error);
      }
    };

    void platformServices.window.getActivityState()
      .then((isActive) => {
        activityStateKnownRef.current = true;
        lastActivityStateRef.current = isActive;
        setAnalyticsActivity(isActive);
        if (isActive) {
          void touchIfActive(true);
        }
      })
      .catch((error) => {
        console.warn('[App] Failed to read window activity state', error);
      });

    const unsubscribe = platformServices.window.onActivityChanged((isActive) => {
      const stateChanged = activityStateKnownRef.current
        && lastActivityStateRef.current !== isActive;

      if (isActive) {
        setAnalyticsActivity(true);
        if (stateChanged) {
          trackEvent('app_foregrounded');
        }
      } else {
        if (stateChanged) {
          trackEvent('app_backgrounded');
        }
        setAnalyticsActivity(false);
      }

      activityStateKnownRef.current = true;
      lastActivityStateRef.current = isActive;

      if (isActive) {
        void touchIfActive();
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [persistSessionActivity, platformServices.window, session]);

  const handleToggleDemo = useCallback(() => {
    if (!DEMO_MODE_AVAILABLE) return; // dev-only safeguard — never activates in production
    setDemoMode((previous) => {
      const next = !previous;
      trackEvent('demo_mode_toggled', {
        demo_mode: next,
      });
      return next;
    });
  }, []);

  // Global shortcut: Cmd/Ctrl+Shift+D toggles demo mode. Dev-only — the listener
  // isn't even registered in production builds.
  useEffect(() => {
    if (!DEMO_MODE_AVAILABLE) return;
    function handleKeyDown(e: KeyboardEvent) {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.shiftKey && (e.key === 'd' || e.key === 'D')) {
        e.preventDefault();
        handleToggleDemo();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleToggleDemo]);

  const handleReplayOnboarding = useCallback(() => {
    trackEvent('onboarding_restarted', {
      source: 'sidebar',
    });
    setOnboardingCompleted(false);
  }, []);

  const handleUpdaterAction = useCallback(async () => {
    try {
      trackEvent('updater_action_clicked', {
        updater_state: updaterStatus.state,
      });

      if (updaterStatus.state === 'downloaded') {
        await platformServices.updater.restartToInstall();
        return;
      }

      if (platformServices.target !== 'desktop') return;

      const nextStatus = await platformServices.updater.checkForUpdates();
      setUpdaterStatus(nextStatus);
    } catch (error) {
      console.error('[App] Updater action failed', error);
    }
  }, [platformServices, updaterStatus.state]);

  useEffect(() => {
    if (!session) {
      return;
    }

    setAnalyticsUserProperties({
      language: session.language || 'en',
      user_role: session.isManager ? 'manager' : 'employee',
    });
    void flushAnalytics();
  }, [session]);

  const appContentVisible = !splashVisible || splashExiting;

  return (
    <>
      {splashVisible && (
        <SplashScreen
          quiet={quietSplash}
          exiting={splashExiting}
          onFinished={handleSplashFinished}
        />
      )}
      {!loading && (
        <div className="app-container" style={{ opacity: appContentVisible ? 1 : 0, transition: 'opacity 0.18s ease-out' }}>
          {platformServices.target === 'desktop' && (
            <Titlebar
              demoMode={demoMode}
              isLoggedIn={!!session}
              canGoBack={navControls?.canGoBack}
              canGoForward={navControls?.canGoForward}
              onBack={navControls?.onBack}
              onForward={navControls?.onForward}
              updaterStatus={updaterStatus}
              onUpdaterAction={handleUpdaterAction}
            />
          )}
          <Suspense fallback={<AppScreenFallback />}>
            {session ? (
              <AppDataProvider key={`${session.company}:${session.email}:${demoMode}`} scope={`${session.company}:${session.email}:${demoMode}`} watchConnection={demoMode ? undefined : watchRtdbReconnect}>
              <FreshnessNotice />
              {!session.isManager ? (
                <EmployeeShell
                  session={session}
                  onLogout={handleLogout}
                  demoMode={demoMode}
                  onNavControlsReady={setNavControls}
                  onReady={markScreenReady}
                />
              ) : onboardingCompleted === false ? (
                <OnboardingFlow
                  session={session}
                  onComplete={() => setOnboardingCompleted(true)}
                  onReady={markScreenReady}
                />
              ) : (
                <MainShell
                  session={session}
                  onLogout={handleLogout}
                  demoMode={demoMode}
                  onToggleDemo={handleToggleDemo}
                  onNavControlsReady={setNavControls}
                  onReplayOnboarding={handleReplayOnboarding}
                  onReady={markScreenReady}
                />
              )}
              </AppDataProvider>
            ) : (
              <LoginScreen
                onLogin={handleLogin}
                initialErrorMessage={loginErrorMessage}
                onReady={markScreenReady}
              />
            )}
          </Suspense>
        </div>
      )}
      {!loading && updaterStatus.state === 'downloaded' && updaterStatus.version && !updatePopupDismissed && (
        <Suspense fallback={null}>
          <UpdatePopup
            version={updaterStatus.version}
            changelog={updaterStatus.changelog}
            action={platformServices.target === 'web' ? 'refresh' : 'restart'}
            onRestart={handleUpdaterAction}
            onDismiss={() => setUpdatePopupDismissed(true)}
          />
        </Suspense>
      )}
    </>
  );
}

function AppScreenFallback() {
  return (
    <div className="flex flex-1 items-center justify-center bg-white">
      <div className="w-9 h-9 rounded-full border-2 border-purple/20 border-t-purple spinner" />
    </div>
  );
}
